import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth, authorizedAxios } from "@moonscape/shared";
import { minutes } from "@/utils";

interface ActivePromo {
  active: boolean;
  promo?: {
    id: number;
    slug: string;
    name: string;
    type: string;
    discountPercent?: number;
    discountCents?: number;
    stripePromoCode?: string;
    applicablePlans?: string[];
  };
  timing?: {
    startedAt: string;
    expiresAt: string;
    secondsRemaining: number;
  };
  source?: string;
  userPromoId?: number;
}

interface StartPromoResponse {
  success?: boolean;
  alreadyAssigned?: boolean;
  userPromoId: number;
  expiresAt?: string;
  secondsRemaining?: number;
  claimed?: boolean;
  promo?: {
    slug: string;
    name: string;
    discountPercent?: number;
    stripePromoCode?: string;
  };
}

export const useActivePromoQueryKey = ["active-promo"];

export const useActivePromo = () => {
  const { isAuthenticated, getToken } = useAuth();
  const queryClient = useQueryClient();

  const fetchActivePromo = async (): Promise<ActivePromo> => {
    const token = await getToken();
    const res = await authorizedAxios(token).get("/promos/active");
    return res.data;
  };

  const {
    isLoading,
    data: activePromo,
    error,
    refetch,
  } = useQuery({
    queryKey: useActivePromoQueryKey,
    queryFn: fetchActivePromo,
    enabled: isAuthenticated,
    staleTime: 1 * minutes,
    refetchInterval: 30 * 1000, // Refetch every 30s to update countdown
  });

  const startPromoMutation = useMutation({
    mutationFn: async ({
      promoSlug,
      source,
      metadata,
    }: {
      promoSlug: string;
      source: string;
      metadata?: Record<string, string>;
    }): Promise<StartPromoResponse> => {
      const token = await getToken();
      const res = await authorizedAxios(token).post("/promos/start", {
        promoSlug,
        source,
        metadata,
      });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: useActivePromoQueryKey });
    },
  });

  const claimPromoMutation = useMutation({
    mutationFn: async ({
      userPromoId,
      planPurchased,
    }: {
      userPromoId: number;
      planPurchased?: string;
    }) => {
      const token = await getToken();
      const res = await authorizedAxios(token).post("/promos/claim", {
        userPromoId,
        planPurchased,
      });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: useActivePromoQueryKey });
    },
  });

  const startPromo = (promoSlug: string, source: string, metadata?: Record<string, string>) => {
    startPromoMutation.mutate({ promoSlug, source, metadata });
  };

  const claimPromo = (userPromoId: number, planPurchased?: string) => {
    claimPromoMutation.mutate({ userPromoId, planPurchased });
  };

  return {
    isLoading,
    error,
    activePromo,
    hasActivePromo: activePromo?.active ?? false,
    promo: activePromo?.promo,
    timing: activePromo?.timing,
    refetch,
    startPromo,
    claimPromo,
    isStarting: startPromoMutation.isPending,
    isClaiming: claimPromoMutation.isPending,
  };
};
